// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import SalesOrderLineInterface from '@/interfaces/SalesOrderLineInterface';
import PluginServices from '@/services/plugin-services';
import ClosePersonalisationModal from '@/components/modals/close-personalisation-modal/close-personalisation-modal';
import FinishPersonalisationModal from '@/components/modals/finish-personalisation-modal/finish-personalisation-modal';
import { getStorage } from '@/utility/storage';
import RecipeInterface from '@/interfaces/Recipe';
import { toast } from 'sonner';

const DEVELOPMENT_MODE = false;
const LOCAL_ORIGIN = 'http://192.168.10.234:8080';
const VIEWPORT_SETTLE_DELAY = 200;

interface PersonalisationModalProps {
  open: boolean;
  onGoBack: () => void;
  onConfirm: (recipe: RecipeInterface) => void;
  line: SalesOrderLineInterface;
  isSubmitting?: boolean;
}

interface PluginEvents {
  authenticationFailed?: boolean;
  isLoading?: boolean;
  missingParameters?: boolean;
  nothingAdded?: boolean;
  recipe?: unknown;
  isLoadingRecipe?: boolean;
  onFinishButtonClick?: boolean;
  onCloseEditorButtonClick?: boolean;
}

interface PluginMessage {
  plugin?: {
    events?: PluginEvents;
  };
}

type PluginStatus = 'loading' | 'authorizing' | 'ready' | 'error';

const getProductId = (line: SalesOrderLineInterface) => line.Item_No_2.split(' ').slice(0, 2).join('+');

export default function PersonalisationModal({
  open,
  onGoBack,
  onConfirm,
  line,
  // isSubmitting = false,
}: PersonalisationModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const authorizationPromiseRef = useRef<Promise<boolean> | null>(null);
  const isAuthorizedRef = useRef(false);

  const [status, setStatus] = useState<PluginStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const productId = getProductId(line);
  const [isLoadingRecipe, setIsloadingRecipe] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [visibleViewport, setVisibleViewport] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else {
      setIsCloseModalOpen(false);
      setIsFinishModalOpen(false);
    }
  }, [open]);

  const postPluginMessage = useCallback((payload: object) => {
    const pluginWindow = iframeRef.current?.contentWindow;

    if (!pluginWindow) return false;

    pluginWindow.postMessage(
      payload,
      DEVELOPMENT_MODE ? new URL('http://192.168.10.234:8080').origin : new URL(getStorage('pluginUrl')).origin,
    );

    return true;
  }, []);

  const authorizePlugin = useCallback(() => {
    if (isAuthorizedRef.current) return Promise.resolve(true);
    if (authorizationPromiseRef.current) return authorizationPromiseRef.current;

    setStatus('authorizing');
    setErrorMessage('');

    // TODO -> CALL SIGNATURE EACH TIME BEFORE OPENING THE PLUGIN!!
    const authorizationPromise = PluginServices.getSignature()
      .then(({ data }) => {
        if (!data.sig) {
          throw new Error('The signature response did not include a signature.');
        }

        const posted = postPluginMessage({
          plugin: {
            auth: {
              key: getStorage('key'),
              sig: data.sig,
              pid: productId,
              cid: getStorage('clid'),
            },
          },
        });

        if (!posted) {
          throw new Error('The personalisation editor is not available.');
        }

        isAuthorizedRef.current = true;
        setStatus('ready');

        return true;
      })
      .catch((error: unknown) => {
        isAuthorizedRef.current = false;
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Unable to authorize the personalisation editor.');

        return false;
      })
      .finally(() => {
        authorizationPromiseRef.current = null;
      });

    authorizationPromiseRef.current = authorizationPromise;

    return authorizationPromise;
  }, [postPluginMessage, productId]);

  useEffect(() => {
    if (!open) return;

    isAuthorizedRef.current = false;
    authorizationPromiseRef.current = null;
    setStatus('loading');
    setErrorMessage('');

    const handlePluginMessage = (event: MessageEvent<PluginMessage>) => {
      const pluginWindow = iframeRef.current?.contentWindow;
      const pluginOrigin = new URL(getStorage('pluginUrl')).origin;

      const isValidOrigin =
        event.origin === new URL(getStorage('pluginUrl')).origin || (DEVELOPMENT_MODE && event.origin === LOCAL_ORIGIN);

      if (isValidOrigin === false || !pluginWindow || event.source !== pluginWindow) return;

      // LOCAL_ORIGIN DEVELOPMENT_MODE

      // if (event.origin !== new URL(getStorage('pluginUrl')).origin || !pluginWindow || event.source !== pluginWindow) {
      //   return;
      // }

      const events = event.data?.plugin?.events;

      if (!events) return;

      if (events.isLoading === false) {
        void authorizePlugin();
      }

      if (events.onFinishButtonClick) {
        handleFinishPersonalisation();
      }

      if (events.onCloseEditorButtonClick) {
        handleCloseEditor();
      }

      if (events.authenticationFailed) {
        isAuthorizedRef.current = false;
        setStatus('error');
        setErrorMessage('The personalisation editor could not be authorized.');
      }

      if (events.missingParameters) {
        setStatus('error');
        setErrorMessage('The personalisation editor is missing required product information.');
      }

      if (events.nothingAdded) {
        toast.info('Missing personalisation', {
          description: 'Please add a personalisation to continue.',
          duration: 5000,
        });

        setErrorMessage('Add a personalisation before continuing.');
      }

      if (events.recipe !== undefined) {
        setIsloadingRecipe(false);

        if (events.recipe === false) {
          setErrorMessage('Add a personalisation before continuing.');
          return;
        }

        const recipeEvent = events.recipe as {
          product_id?: string;
          recipe_id?: string;
          views?: Array<{ image?: string }>;
        } | null;

        if (!recipeEvent) return;

        const { product_id, recipe_id, views = [] } = recipeEvent;
        const [firstView] = views;
        const { image = '' } = firstView ?? {};
        const recipe = { recipe_id, product_id, image };

        onConfirm(recipe as RecipeInterface);
      }

      if (typeof events.isLoadingRecipe === 'boolean') {
        setIsloadingRecipe(events.isLoadingRecipe);
      }
    };

    window.addEventListener('message', handlePluginMessage);

    return () => window.removeEventListener('message', handlePluginMessage);
  }, [authorizePlugin, onConfirm, open, productId]);

  const handleIframeLoad = () => {
    isAuthorizedRef.current = false;
    authorizationPromiseRef.current = null;
    setStatus('loading');
    setErrorMessage('');
  };

  const handleAccept = () => {
    const posted = postPluginMessage({
      plugin: {
        events: {
          requestRecipe: true,
        },
      },
    });

    if (!posted) {
      setStatus('error');
      setErrorMessage('The personalisation editor is not available.');
    }
  };

  // const statusText = {
  //   loading: 'Loading editor…',
  //   authorizing: 'Authorizing editor…',
  //   ready: 'Editor ready',
  //   error: errorMessage || 'Editor unavailable',
  // }[status];

  const handleCloseEditor = () => {
    // Radix hides the editor before the confirmation's autofocus runs.
    // Release iframe focus first so it is never inside an aria-hidden subtree.
    iframeRef.current?.blur();
    setIsCloseModalOpen(true);
  };

  const handleFinishPersonalisation = () => {
    iframeRef.current?.blur();
    setIsFinishModalOpen(true);
  };

  const handleConfirmationCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    if (open && iframeRef.current?.isConnected) iframeRef.current.focus();
  };

  const handleConfirmCloseEditor = () => {
    setIsCloseModalOpen(false);
    onGoBack();
  };

  const handleConfirmFinishPersonalisation = () => {
    setIsFinishModalOpen(false);
    handleAccept();
    // finish...
  };

  return (
    <>
      <DialogPrimitive.Root modal open={open}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[100] touch-none overscroll-none bg-black/70 backdrop-blur-[2px]" />

          <DialogPrimitive.Content
            data-personalisation-editor="true"
            // style={
            //   visibleViewport
            //     ? {
            //         top: `calc(${visibleViewport.top}px + var(--editor-gap))`,
            //         left: `calc(${visibleViewport.left}px + var(--editor-gap))`,
            //         width: `calc(${visibleViewport.width}px - 2 * var(--editor-gap))`,
            //         height: `calc(${visibleViewport.height}px - 2 * var(--editor-gap))`,
            //       }
            //     : undefined
            // }
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              event.currentTarget.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
            }}
            className="fixed
                    z-[101]
                    isolate
                    flex
                    min-h-0
                    min-w-0
                    max-w-none
                    flex-col
                    overflow-hidden
                    overscroll-none
                    touch-auto
                    border
                    border-border
                    bg-background
                    p-0
                    outline-none

                    left-0
                    top-0
                    h-[100dvh]
                    w-full
                    [--editor-gap:0px]

                    sm:[--editor-gap:20px]
                    sm:left-[20px]
                    sm:top-[20px]
                    sm:h-[calc(100dvh-40px)]
                    sm:w-[calc(100%-40px)]
                    sm:shadow-[0_18px_50px_rgba(10,10,10,0.22)]"
          >
            <DialogPrimitive.Title className="sr-only">
              Personalise {line.Sold_As_Description || line.Description || productId}
            </DialogPrimitive.Title>

            <DialogPrimitive.Description className="sr-only">
              Use the editor to create a personalisation for the selected product.
            </DialogPrimitive.Description>

            <div className="pointer-events-auto relative z-0 min-h-0 w-full touch-auto flex-1 overflow-hidden p-0">
              <iframe
                key={productId}
                ref={iframeRef}
                className="pointer-events-auto relative z-0 block h-full w-full touch-auto border-0"
                onLoad={handleIframeLoad}
                src={DEVELOPMENT_MODE ? 'http://192.168.10.234:8080' : getStorage('pluginUrl')}
                title={`Personalisation editor for ${line.Sold_As_Description || line.Description || productId}`}
              />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <ClosePersonalisationModal
        open={open && isCloseModalOpen}
        onCloseAutoFocus={handleConfirmationCloseAutoFocus}
        onCancel={() => setIsCloseModalOpen(false)}
        onConfirm={handleConfirmCloseEditor}
      />

      <FinishPersonalisationModal
        open={open && isFinishModalOpen}
        onCloseAutoFocus={handleConfirmationCloseAutoFocus}
        onCancel={() => setIsFinishModalOpen(false)}
        onConfirm={handleConfirmFinishPersonalisation}
      />
    </>
  );
}

{
  /* <footer className="pointer-events-auto relative z-10 flex h-[44px] shrink-0 items-center justify-start gap-[8px] border-b border-border bg-background px-[8px] sm:h-[48px] sm:px-[12px]">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-full h-[6px] bg-[linear-gradient(to_top,rgba(0,0,0,0.24),rgba(0,0,0,0.08)_45%,transparent)]"
              data-testid="personalisation-footer-shadow"
            />

            <button
              aria-busy={isSubmitting}
              className="button-screwfix-success inline-flex h-[32px] min-h-0 items-center justify-center gap-[6px] !px-[12px] [font-family:archivo_narrowbold,sans-serif] !text-[16px] font-bold disabled:pointer-events-none disabled:opacity-60 sm:h-[34px]"
              disabled={isLoadingRecipe}
              onClick={handleFinishPersonalisation}
              type="button"
            >
              {isSubmitting ? <LoaderCircle aria-hidden="true" className="h-[14px] w-[14px] animate-spin" /> : null}

              {isSubmitting ? 'Submitting…' : 'Finish'}
            </button>

            <button
              className="button-screwfix-secondary inline-flex h-[32px] min-h-0 items-center justify-center !px-[12px] [font-family:archivo_narrowbold,sans-serif] !text-[16px] font-bold disabled:pointer-events-none disabled:opacity-60 sm:h-[34px]"
              disabled={isLoadingRecipe || isSubmitting}
              onClick={handleCloseEditor}
              type="button"
            >
              Close editor
            </button>

            <span
              aria-live="polite"
              className={`min-w-0 flex-1 truncate invisible text-[16px] sm:text-[18px] ${
                status === 'error' ? 'text-danger' : 'text-muted-foreground'
              }`}
              role={status === 'error' ? 'alert' : 'status'}
            >
              {statusText}
            </span>
          </footer> */
}
// useLayoutEffect(() => {
//   if (!open) return;

//   // The keyboard can resize and pan the visual viewport without resizing 100dvh.
//   // Use the parent viewport: an iframe's visual viewport only measures the frame.
//   const viewport = window.visualViewport;
//   let viewportTimer: number | undefined;
//   const updateViewport = () => {
//     const nextViewport = {
//       top: Math.round(viewport?.offsetTop ?? 0),
//       left: Math.round(viewport?.offsetLeft ?? 0),
//       width: Math.round(viewport?.width ?? window.innerWidth),
//       height: Math.round(viewport?.height ?? window.innerHeight),
//     };
//     setVisibleViewport((currentViewport) => {
//       if (
//         currentViewport?.top === nextViewport.top &&
//         currentViewport?.left === nextViewport.left &&
//         currentViewport?.width === nextViewport.width &&
//         currentViewport?.height === nextViewport.height
//       )
//         return currentViewport;
//       return nextViewport;
//     });
//   };
//   const scheduleViewportUpdate = () => {
//     // iOS briefly reports intermediate sizes/pan offsets while changing keyboards.
//     // Resizing the iframe for each one can disturb its focused mobile text editor.
//     window.clearTimeout(viewportTimer);
//     viewportTimer = window.setTimeout(updateViewport, VIEWPORT_SETTLE_DELAY);
//   };

//   updateViewport();
//   viewport?.addEventListener('resize', scheduleViewportUpdate);
//   viewport?.addEventListener('scroll', scheduleViewportUpdate);
//   window.addEventListener('resize', scheduleViewportUpdate);

//   return () => {
//     window.clearTimeout(viewportTimer);
//     viewport?.removeEventListener('resize', scheduleViewportUpdate);
//     viewport?.removeEventListener('scroll', scheduleViewportUpdate);
//     window.removeEventListener('resize', scheduleViewportUpdate);
//   };
// }, [open]);
